import { useEffect, useRef, useState } from "react";
import { useIsFetching } from "@tanstack/react-query";
import i18n, { getActiveLang } from "../i18n";
import { MeridianSplash } from "./ui/MeridianSplash";

// After a switch, how long to wait for a content refetch to actually start
// before concluding the target language was cached (so no loader is needed).
const GRACE_MS = 300;
// Safety net: clear the overlay even if a content refetch hangs.
const MAX_MS = 4000;
// Language-keyed content query roots refetched on a language switch.
const CONTENT_KEYS = ["content", "card-meanings", "legal", "translations"];

type Phase = "idle" | "pending" | "visible";

// Full-screen splash shown while switching language. Decoupled from the toggle:
// it reacts to i18next's `languageChanged` event and watches React Query's
// in-flight content fetches. The overlay appears ONLY when the switch actually
// triggers a content refetch — switching to an already-cached language shows
// nothing, so there is no gratuitous flash. Once shown, it clears as soon as
// the refetch settles.
export function LanguageSwitchOverlay() {
  const [phase, setPhase] = useState<Phase>("idle");
  // Bumped on each real switch so the phase effects restart even mid-switch.
  const [switchId, setSwitchId] = useState(0);
  // Stored as the normalized 2-letter code; compared against the normalized
  // `languageChanged` payload, so the ref is a plain string, not the Lang union.
  const prevLang = useRef<string>(getActiveLang());

  const fetching = useIsFetching({
    predicate: (query) =>
      CONTENT_KEYS.includes(query.queryKey[0] as string),
  });

  // Trigger: a real language change (boot resolution to the same lang is a no-op).
  useEffect(() => {
    const onChange = (lng: string) => {
      const next = lng.split("-")[0].toLowerCase();
      if (next === prevLang.current) return;
      prevLang.current = next;
      setPhase("pending");
      setSwitchId((id) => id + 1);
    };
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, []);

  // Detect: only show the splash if a content refetch starts within the grace
  // window. If nothing fetches (cached language), drop back to idle silently.
  useEffect(() => {
    if (phase !== "pending") return;
    if (fetching > 0) {
      setPhase("visible");
      return;
    }
    const grace = setTimeout(
      () => setPhase((p) => (p === "pending" ? "idle" : p)),
      GRACE_MS,
    );
    return () => clearTimeout(grace);
  }, [phase, fetching, switchId]);

  // Safety: never let the splash hang if a refetch stalls.
  useEffect(() => {
    if (phase !== "visible") return;
    const max = setTimeout(() => setPhase("idle"), MAX_MS);
    return () => clearTimeout(max);
  }, [phase, switchId]);

  // Dismiss as soon as the content refetch settles.
  useEffect(() => {
    if (phase === "visible" && fetching === 0) {
      setPhase("idle");
    }
  }, [phase, fetching]);

  if (phase !== "visible") return null;
  return <MeridianSplash fading={false} />;
}
